/**
 * ContractorContactDetail.tsx — F12.M Etap 1.5.
 *
 * Fullscreen detail kontaktu kontrahenta. 2 ścieżki tworzenia:
 *  - /contractor-contacts/new — picker kontrahenta required
 *  - /contractor-contacts/new?contractorId=X — prefill z detail kontrahenta
 *
 * Edycja: PATCH /contractors/:cId/contacts/:id (nested endpoint, contractorId
 * z response GET).
 *
 * Tabs (kartoteka-blokify: poza „Dane" zakładki to PRZENOŚNE BLOKI — dispatch po
 * TYPIE przez contextRenderers, usuwalne i dodawalne z palety):
 *   - data — ContactDataTab (dynamic z layout_config; honoruje nakładkę tab_overrides)
 *   - contractor_contact_audit_view — SystemModuleAuditTab (fallback audit_log_view)
 *
 * Save flow — JEDEN zapis (pola dodatkowe nie mają osobnej zakładki ani osobnego PUT):
 *   - create: JEDEN POST /contractors/:cId/contacts (pola + extValues w tym samym body)
 *   - edit: JEDEN PATCH /contractors/:cId/contacts/:id (pola + extValues)
 *
 * Przycisk SQL (klocek układu `sql_button`, contextRenderer): zadanie w tle na kontakcie. Edycja — klik
 * najpierw zapisuje (`useSaveBeforeDecision`), podgląd — rusza od razu, nowy rekord — utworzenie →
 * zadanie → przejście. Na czas zadania pasek blokady, „Edytuj” i „Usuń” nieaktywne, a zapis / usunięcie
 * odrzucone 409 `RECORD_LOCKED_BY_RUN` dają czytelny komunikat (mirror ContractorDetail).
 */

import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { isAxiosError } from 'axios';
import { ArrowLeft, Pencil, Save, Star, Trash2, X as XIcon } from 'lucide-react';

import { Badge } from '@/components/Badge';
import { Button } from '@/components/Button';
import { ConfirmDialog } from '@/components/ConfirmDialog';
import {
  buildContactCoreRecordValues,
  ContactDataTab,
  type ContactFormData,
  EMPTY_CONTACT_FORM,
} from '@/components/contractor-contacts/ContactDataTab';
import { contactEngineConfig } from '@/components/contractor-contacts/contactEngineConfig';
import { Dropdown } from '@/components/Dropdown';
import { tryRenderLayoutElement } from '@/components/fields/LayoutElementRegistry';
import {
  type ContextRenderers,
  RecursiveFormRenderer,
} from '@/components/form/RecursiveFormRenderer';
import { IconButton } from '@/components/IconButton';
import { collectEngineFieldErrors } from '@/components/kartoteki/engine/engine-config';
import { useKartotekaRenderField } from '@/components/kartoteki/engine/useKartotekaRenderField';
import { NewRecordDefaultsStatus } from '@/components/record-defaults/NewRecordDefaultsStatus';
import { RecordRunLockBanner } from '@/components/record-procedures/RecordRunLockBanner';
import {
  collectSqlButtonLabels,
  parseSqlButtonSettings,
} from '@/components/record-procedures/sql-button-settings';
import { SqlButton } from '@/components/record-procedures/SqlButton';
import { Spinner } from '@/components/Spinner';
import { SystemModuleAuditTab } from '@/components/system-modules/SystemModuleAuditTab';
import { Tabs } from '@/components/Tabs';
import { useToast } from '@/contexts/ToastContext';
import { useFieldErrors } from '@/hooks/useFieldErrors';
import { applyCoreDefaults, useNewRecordDefaults } from '@/hooks/useNewRecordDefaults';
import { usePermission } from '@/hooks/usePermission';
import { useRecordExtValues } from '@/hooks/useRecordExtValues';
import { useRecordProcedureRuns } from '@/hooks/useRecordProcedureRuns';
import { useSaveBeforeDecision } from '@/hooks/useSaveBeforeDecision';
import { useSystemModuleActiveLayout } from '@/hooks/useSystemModuleActiveLayout';
import { useWithMenuItem } from '@/hooks/useWithMenuItem';
import { api } from '@/lib/api';
import { extractErrorCode } from '@/lib/errors';
import { useSystemModuleFields } from '@/lib/ext-list-columns';
import {
  buildFieldIssueMessages,
  buildValidationToastMessage,
  resolveFieldLabel,
} from '@/lib/validation-toast';

interface ContactPayload {
  contact: ContactFormData & {
    id: string;
    contractorName: string;
    contractorShortName: string | null;
    gcrecord: string | null;
    createdAt: string;
    updatedAt: string;
    // Surowe wartości pól dodatkowych — dołączone przez serwer do odpowiedzi GET rekordu.
    extValues?: Record<string, unknown>;
  };
}

interface ContractorOption {
  id: string;
  name: string;
}

export function ContractorContactDetail() {
  const { t } = useTranslation();
  const { id } = useParams<{ id?: string }>();
  const isCreate = !id || id === 'new';
  const navigate = useNavigate();
  // Powrót na listę niesie jej kontekst — patrz useWithMenuItem.
  const withMenuItem = useWithMenuItem();
  const toast = useToast();
  const queryClient = useQueryClient();
  const [searchParams, setSearchParams] = useSearchParams();
  // Podswietlenie pol odrzuconych przez backendowy VALIDATION_ERROR (czerwona
  // ramka, bez tekstu — komunikat zostaje w toascie). Patrz onError ponizej.
  const fe = useFieldErrors();

  // F12.N E1.T5b: layout w nowym kształcie + role context dla filtrowania zakładek.
  const { layoutConfig, myRoleId, isSuperUser } =
    useSystemModuleActiveLayout('contractor_contacts');

  const prefilledContractorId = searchParams.get('contractorId');

  const initialTab = searchParams.get('tab') ?? 'data';
  const [tab, setTabRaw] = useState<string>(initialTab);

  // F12.N D4 save-and-continue fix: synchronizuj `tab` z URL — patrz ContractorDetail.tsx
  // dla pełnego opisu (React Router 6 nie remountuje na SPA navigate `/new → /:id`).
  useEffect(() => {
    const urlTab = searchParams.get('tab') ?? 'data';
    if (urlTab !== tab) setTabRaw(urlTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  // F12.N D4 save-and-continue — patrz ContractorDetail.tsx dla pełnego opisu.
  // Uwaga: niejawny POST wymaga wybranego kontrahenta (`contractorId` w form). Pre-walidacja:
  // jeśli admin nie wybrał — toast „Wybierz kontrahenta" przez `onError CONTRACTOR_REQUIRED`,
  // admin zostaje na 'data'.
  const pendingTabRef = useRef<string | null>(null);

  const setTab = (next: string) => {
    if (isCreate && tab === 'data' && next !== 'data' && !saveMutation.isPending) {
      pendingTabRef.current = next;
      // Format-gate przez handleSave — gdy blokuje, nie zostawiaj pendingTaba.
      if (!handleSave()) pendingTabRef.current = null;
      return;
    }
    setTabRaw(next);
    const params = new URLSearchParams(searchParams);
    if (next === 'data') params.delete('tab');
    else params.set('tab', next);
    setSearchParams(params, { replace: true });
  };

  const [editing, setEditing] = useState(isCreate);
  const [form, setForm] = useState<ContactFormData>({
    ...EMPTY_CONTACT_FORM,
    contractorId: prefilledContractorId ?? '',
  });
  const [contractorName, setContractorName] = useState<string>('');
  const [confirmDelete, setConfirmDelete] = useState(false);

  const detailQ = useQuery({
    queryKey: ['contractor-contact', id],
    queryFn: async () => {
      const res = await api.get<ContactPayload>(`/contractor-contacts/${id}`);
      return res.data;
    },
    enabled: !isCreate && !!id,
  });

  // Przycisk SQL (klocek `sql_button`): zadania wykonywane w tle na tym kontakcie. Stan (aktywne zadanie
  // + ostatnie per procedura) odpytywany co ~2 s tylko przy aktywnym zadaniu; po jego końcu komunikat w
  // rogu i odświeżenie rekordu (zapytanie mogło go zmienić). Przy tworzeniu brak rekordu. Napisy
  // przycisków z układu służą komunikatom i paskowi blokady (zadanie zna tylko nazwę procedury).
  const sqlButtonLabels = useMemo(() => collectSqlButtonLabels(layoutConfig), [layoutConfig]);
  const runs = useRecordProcedureRuns('contractor_contact', isCreate ? undefined : id, {
    labels: sqlButtonLabels,
    onFinished: () => {
      void queryClient.invalidateQueries({ queryKey: ['contractor-contact', id] });
      void queryClient.invalidateQueries({ queryKey: ['contractor-contacts'] });
      void queryClient.invalidateQueries({ queryKey: ['additional-records'] });
      void queryClient.invalidateQueries({
        queryKey: ['system-module-audit', 'contractor_contacts', id],
      });
    },
  });
  // Prawo uruchomienia = prawo edycji kontaktu (`crm_core.contractors.contacts.manage` w aktywnej firmie).
  // Bramka w panelu to podpowiedź (lista kodów nie zna zawężeń) — o odmowie rozstrzyga serwer. Kontakt
  // usunięty miękko ma ukryte „Edytuj", więc przycisk też jest wyszarzony.
  const contactEditPermission = usePermission('crm_core.contractors.contacts.manage');
  const canRunSqlButton = contactEditPermission.allowed && detailQ.data?.contact.gcrecord == null;
  // Przycisk kliknięty na NOWYM rekordzie: najpierw utworzenie (zwykły zapis), potem zadanie z id świeżo
  // utworzonego kontaktu, dopiero potem przejście pod jego adres (patrz `saveMutation.onSuccess`).
  // `settle` kończy oczekiwanie przycisku (kręciołek), także przy porażce zapisu.
  const pendingCreateRunRef = useRef<{ procedureId: string; settle: () => void } | null>(null);

  // Wartości domyślne pól (Ustawienia kartoteki → Pola) dla formularza nowego kontaktu — pola
  // pokazujemy dopiero po ich wczytaniu (patrz `NewRecordDefaultsStatus` niżej).
  const recordDefaults = useNewRecordDefaults('contractor_contacts', isCreate);

  // Pola dodatkowe w zakładce „Dane": stan lokalny zasilany wartością z odpowiedzi GET rekordu
  // (`contact.extValues`, bez osobnego żądania). Pełny kontrakt synchronizacji (w tym ochrona
  // niezapisanych edycji przed odświeżeniem w tle) w nagłówku `useRecordExtValues.ts`. Przy
  // tworzeniu „wartością serwera" są wartości domyślne pól dodatkowych.
  const { extValues, handleExtChange, markSaved, snapshotEdits, discardEdits } = useRecordExtValues(
    {
      recordId: isCreate ? undefined : id,
      serverExtValues: isCreate ? recordDefaults.ext : detailQ.data?.contact.extValues,
      serverVersion: detailQ.dataUpdatedAt,
    },
  );

  // F12.N D4 save-and-continue: tabs wyciągnięte z layoutu — dla osobnego `<Tabs>` w trybie create.
  // T2: element `tabs` może NIE być pierwszy (przed nim stoją elementy view-level:
  // page-header/highlight) — szukamy po typie, nie po indeksie [0].
  const tabsFromLayout = useMemo(() => {
    const tabsElement = layoutConfig?.elements?.find((el) => el.type === 'tabs');
    if (tabsElement?.type === 'tabs' && tabsElement.tabs) {
      return tabsElement.tabs
        .filter((tt) => !tt.hidden)
        .slice()
        .sort((a, b) => (a.sortOrder ?? Infinity) - (b.sortOrder ?? Infinity));
    }
    return null;
  }, [layoutConfig]);

  // T2: elementy view-level (rodzeństwo `tabs`) renderowane NAD paskiem zakładek w
  // trybie create (parytet z edit-mode RecursiveFormRenderer, który mapuje elements po kolejności).
  const viewLevelFromLayout = useMemo(
    () => layoutConfig?.elements?.filter((el) => el.type !== 'tabs') ?? [],
    [layoutConfig],
  );

  // Silnik formatek (keystone unifikacja-formatek — mirror ContractorDetail/CompanyDetail) —
  // pola core kontaktu przez `contactEngineConfig`. `hasError` daje ramkę, `getError` tekst
  // błędu pod polem, `clearError` gaśnięcie przy edycji (ujednolicenie walidacji 2026-09).
  const { renderField, fields: kartotekaFields } = useKartotekaRenderField<ContactFormData>(
    contactEngineConfig,
    {
      form,
      onChange: setForm,
      extValues,
      onExtChange: handleExtChange,
      editable: editing || isCreate,
      isCreate,
      hasError: (key) => !!fe.get(String(key)),
      getError: (key) => fe.get(String(key)),
      clearError: (key) => fe.clear(String(key)),
    },
  );

  // Wartości formularza keyed po slugu (core + ext) — do oceny warunków widoczności
  // pól/bloków z kreatora. Przekazywane do ContactDataTab i RecursiveFormRenderer.
  const layoutFormData = useMemo<Record<string, unknown>>(
    () => ({ ...buildContactCoreRecordValues(form), ...extValues }),
    [form, extValues],
  );

  // Mapa rendererów elementów zależnych od kontekstu — wspólna dla silnika
  // (RecursiveFormRenderer) i dedykowanej zakładki „Dane" (ContactDataTab), żeby element
  // „widokowy" postawiony w „Dane" renderował się żywcem, nie jako martwy placeholder z
  // generycznego rejestru (mirror ContractorDetail.tsx).
  const contactContextRenderers: ContextRenderers = {
    // „Historia zmian" jako przenośny blok per kartoteka (`contractor_contact_audit_view`);
    // `audit_log_view` = fallback dla layoutów sprzed blokify.
    contractor_contact_audit_view: () =>
      id ? (
        <SystemModuleAuditTab
          moduleSlug="contractor_contacts"
          recordId={id}
          testIdPrefix="contractor-contact-audit"
        />
      ) : null,
    audit_log_view: () =>
      id ? (
        <SystemModuleAuditTab
          moduleSlug="contractor_contacts"
          recordId={id}
          testIdPrefix="contractor-contact-audit"
        />
      ) : null,
    // Klocek „Przycisk SQL”: zadanie w tle na tym kontakcie. W edycji klik najpierw zapisuje rekord
    // (bramka `useSaveBeforeDecision`; błąd zapisu = zadanie nie rusza), w podglądzie rusza od razu,
    // na nowym rekordzie — utworzenie, zadanie, przejście. Prawo: ten sam sygnał co zapis (serwer i tak
    // sprawdza). „Zapisz" w toku też blokuje przycisk — drugi klik nie może wysłać drugiego zapisu.
    // Identyfikatory testowe per klocek (`sql-button-<id klocka>`): strona może mieć kilka przycisków.
    // Klocek bez procedury / napisu: nic.
    sql_button: ({ element }) => {
      const cfg = parseSqlButtonSettings(element.settings);
      if (cfg === null) return null;
      return (
        <SqlButton
          label={cfg.label}
          procedureId={cfg.procedureId}
          active={runs.active}
          lastRun={runs.lastByProcedure.find((run) => run.procedureId === cfg.procedureId)}
          canEdit={canRunSqlButton}
          starting={runs.starting || saveMutation.isPending}
          beforeStart={isCreate ? undefined : saveBeforeDecision.beforeDecision}
          onStart={(procedureId) => handleSqlButtonStart(procedureId)}
          testIdPrefix={`sql-button-${element.id}`}
        />
      );
    },
  };

  // Lookup kontrahentów dla pickera w create-mode
  const contractorsQ = useQuery<ContractorOption[]>({
    queryKey: ['contractors-lookup-min'],
    queryFn: async () => {
      const res = await api.get<{ contractors: Array<{ id: string; name: string }> }>(
        '/contractors?limit=200',
      );
      return res.data.contractors.map((c) => ({ id: c.id, name: c.name }));
    },
    staleTime: 60_000,
    enabled: isCreate,
  });

  const contactFieldsQ = useSystemModuleFields('contractor_contacts');
  const contactFieldLabel = (field: string) => resolveFieldLabel(contactFieldsQ.data ?? [], field);

  // Sync server → form
  useEffect(() => {
    if (isCreate) {
      // Wartości domyślne nadpisują wartości startowe z kodu; kontrahent z adresu (`?contractorId=`)
      // wygrywa z wartością domyślną — nakładany na końcu.
      setForm({
        ...applyCoreDefaults(EMPTY_CONTACT_FORM, recordDefaults.core),
        contractorId: prefilledContractorId ?? '',
      });
      return;
    }
    if (detailQ.data?.contact) {
      const c = detailQ.data.contact;
      setForm({
        contractorId: c.contractorId,
        title: c.title,
        firstName: c.firstName,
        lastName: c.lastName,
        position: c.position,
        department: c.department,
        email: c.email,
        phone: c.phone,
        mobile: c.mobile,
        preferredChannel: c.preferredChannel,
        language: c.language,
        birthday: c.birthday,
        linkedinUrl: c.linkedinUrl,
        isPrimary: c.isPrimary,
        isDecisionMaker: c.isDecisionMaker,
        gdprConsentAt: c.gdprConsentAt,
        notes: c.notes,
        categoryId: c.categoryId,
        groups: c.groups ?? [],
      });
      setContractorName(c.contractorShortName || c.contractorName);
    }
  }, [detailQ.data, isCreate, recordDefaults.core, prefilledContractorId]);

  // Klik przycisku SQL, gdy formularz jest w edycji: najpierw ten sam zapis co „Zapisz”, zadanie dopiero po
  // jego sukcesie (useSaveBeforeDecision; „zapis już trwa" sprawdza sam hak po kluczu `saveMutationKey`
  // mutacji zapisu tej kopii formularza). `succeeded` / `aborted` są wołane z `saveMutation.onSuccess/onError`.
  const saveBeforeDecision = useSaveBeforeDecision({
    editing,
    recordId: isCreate ? undefined : id,
    save: handleSave,
  });

  const saveMutation = useMutation({
    mutationKey: saveBeforeDecision.saveMutationKey,
    mutationFn: async (data: ContactFormData) => {
      if (!data.contractorId) {
        throw new Error('CONTRACTOR_REQUIRED');
      }
      const payload: Record<string, unknown> = {
        title: data.title,
        firstName: data.firstName,
        lastName: data.lastName,
        position: data.position,
        department: data.department,
        email: data.email || null,
        phone: data.phone,
        mobile: data.mobile,
        preferredChannel: data.preferredChannel,
        language: data.language ?? undefined,
        birthday: data.birthday,
        linkedinUrl: data.linkedinUrl,
        isPrimary: data.isPrimary,
        isDecisionMaker: data.isDecisionMaker,
        notes: data.notes,
        categoryId: data.categoryId,
        groups: data.groups,
      };
      // Atomic-save (mirror ContractorDetail): jeden zapis — extValues jadą w TYM SAMYM body co
      // pola kontaktu (backend zapisuje wszystko w jednej transakcji, saveAggregate); zero
      // osobnego PUT ext-fields.
      if (Object.keys(extValues).length > 0) {
        payload.extValues = extValues;
      }

      // Zapamiętaj, które pola dodatkowe wchodzą do tego zapisu — `markSaved()` w onSuccess
      // zgasi tylko je (edycja w trakcie żądania nie zostanie uznana za zapisaną).
      snapshotEdits();

      let recordId: string;
      if (isCreate) {
        // Backend zwraca pełen obiekt insert'a (NextResponse.json(inserted, { status: 201 })).
        const res = await api.post<{ id: string }>(
          `/contractors/${data.contractorId}/contacts`,
          payload,
        );
        recordId = res.data.id;
      } else {
        await api.patch(`/contractors/${data.contractorId}/contacts/${id}`, payload);
        recordId = id!;
      }
      return { contact: { id: recordId } };
    },
    onSuccess: async (data) => {
      // Udany zapis gasi podświetlenia z poprzedniej nieudanej próby (ramka + tekst).
      fe.clearAll();
      // Pola dodatkowe zapisane razem z kontaktem — od tej chwili wartość serwera (z odświeżonego
      // rekordu) może je nadpisać; bez tego guard niezapisanych edycji trzymałby stare wartości.
      markSaved();
      queryClient.invalidateQueries({ queryKey: ['contractor-contacts'] });
      if (isCreate && data?.contact?.id) {
        // F12.N D4 save-and-continue: dorzucamy ?tab=<next> jeśli zapis był wyzwolony zmianą zakładki.
        const nextTab = pendingTabRef.current;
        pendingTabRef.current = null;
        const suffix = nextTab && nextTab !== 'data' ? `?tab=${nextTab}` : '';
        toast.show({ variant: 'success', title: t('tenant.contractorContacts.toast.created') });
        // Przycisk SQL kliknięty na nowym rekordzie: zadanie rusza TERAZ — z id nowego kontaktu, PRZED
        // przejściem pod jego adres. Porażka zlecenia dostaje własny komunikat; rekord już istnieje,
        // więc przejście i tak następuje.
        if (pendingCreateRunRef.current !== null) {
          await runPendingCreateRun(data.contact.id);
        }
        // Karta „nowy" ma zamienić się w kartę zapisanego rekordu (replaceTab) —
        // reset do podglądu, bo create→detail nie remontuje strony.
        setEditing(false);
        navigate(withMenuItem(`/contractor-contacts/${data.contact.id}${suffix}`), {
          state: { replaceTab: true },
        });
        return;
      }
      toast.show({ variant: 'success', title: t('tenant.contractorContacts.toast.updated') });
      if (id) {
        queryClient.invalidateQueries({ queryKey: ['contractor-contact', id] });
      }
      // `setEditing(false)` i `succeeded()` razem, bez `await` między nimi (wymóg haka). Zadanie przycisku
      // SQL działa na danych już zapisanych w bazie, więc nie czeka na odświeżenie rekordu w formularzu.
      setEditing(false);
      // Zapis wywołany kliknięciem przycisku SQL → teraz startuje zadanie (no-op, gdy zapis poszedł
      // zwykłym „Zapisz"). Tylko gałąź edycji — przy tworzeniu zadanie rusza w gałęzi wyżej.
      saveBeforeDecision.succeeded();
    },
    onError: (err) => {
      // F12.N D4 save-and-continue: błąd → reset pendingTab (admin zostaje na 'data',
      // np. CONTRACTOR_REQUIRED gdy nie wybrał z pickera).
      pendingTabRef.current = null;
      // Zapis wywołany kliknięciem przycisku SQL: każdy błąd kończy sprawę — zadanie się nie uruchomi
      // (w edycji bramka zapomina czekające zlecenie, na nowym rekordzie zapamiętane zadanie jest zdejmowane).
      saveBeforeDecision.aborted();
      settlePendingCreateRun();
      // Podświetlenie pól odrzuconych przez serwer musi zobaczyć KAŻDĄ odpowiedź,
      // zanim jakakolwiek gałąź zrobi wczesny `return` — inaczej stare podświetlenie
      // z poprzedniej próby zostaje na ekranie przy zupełnie innym błędzie (patrz
      // JSDoc `useFieldErrors.ts`).
      // Kolejność komunikatu: (1) polski z issue Zoda, (2) własny komunikat serwera z
      // `details` (bez `issues`), (3) generyk — bez `messages[0]` polskie zdania serwera
      // ginęłyby pod generykiem.
      const fieldMsgs = buildFieldIssueMessages(err, t);
      fe.applyServerError(err, {
        translate: (field, messages) =>
          fieldMsgs[field] ?? messages[0] ?? t('common.validationIssue.generic'),
      });
      if (err instanceof Error && err.message === 'CONTRACTOR_REQUIRED') {
        toast.show({
          variant: 'error',
          title: t('tenant.contractorContacts.toast.contractorRequired'),
        });
        return;
      }
      // Zapis odrzucony, bo na rekordzie trwa zadanie przycisku SQL — czytelny komunikat z opisem zadania
      // zamiast ogólnego „konflikt”; odświeżony stan zadań pokaże pasek blokady.
      if (runs.handleLockedError(err)) return;
      const code = extractErrorCode(err);
      if (code === 'VALIDATION_ERROR') {
        const specific = buildValidationToastMessage(err, contactFieldLabel, t);
        toast.show({
          variant: 'error',
          title: specific ?? t('tenant.contractorContacts.toast.validationError'),
        });
        return;
      }
      if (isAxiosError(err) && err.response?.status === 409) {
        toast.show({ variant: 'error', title: t('tenant.contractorContacts.toast.conflict') });
        return;
      }
      toast.show({ variant: 'error', title: t('tenant.contractorContacts.toast.saveError') });
    },
  });

  const deleteMutation = useMutation({
    mutationFn: async () => {
      if (!form.contractorId || !id) return;
      await api.delete(`/contractors/${form.contractorId}/contacts/${id}`);
    },
    onSuccess: () => {
      toast.show({ variant: 'success', title: t('tenant.contractorContacts.toast.deleted') });
      queryClient.invalidateQueries({ queryKey: ['contractor-contacts'] });
      navigate(withMenuItem('/contractor-contacts'));
    },
    onError: (err) => {
      // Usuwanie odrzucone na czas zadania przycisku SQL — jak przy zapisie (opis zadania + pasek blokady).
      if (runs.handleLockedError(err)) return;
      toast.show({ variant: 'error', title: t('tenant.contractorContacts.toast.deleteError') });
    },
  });

  /** Kończy oczekiwanie przycisku SQL klikniętego na nowym rekordzie bez uruchamiania zadania. */
  function settlePendingCreateRun(): void {
    const pending = pendingCreateRunRef.current;
    pendingCreateRunRef.current = null;
    pending?.settle();
  }

  /**
   * Zleca zadanie zapamiętane przy kliknięciu na nowym rekordzie — z id świeżo utworzonego kontaktu. Firma
   * zlecenia to aktywna firma aplikacji (kontakt nie ma własnej; serwer wyprowadza ją z kontrahenta).
   */
  async function runPendingCreateRun(newContactId: string): Promise<void> {
    const pending = pendingCreateRunRef.current;
    pendingCreateRunRef.current = null;
    if (pending === null) return;
    try {
      await runs.startRun(pending.procedureId, newContactId);
    } finally {
      pending.settle();
    }
  }

  /**
   * Start zadania przycisku SQL. W edycji i podglądzie wołany PO bramce zapisu (edycja: najpierw zwykły
   * zapis, błąd zapisu = zadanie nie startuje; podgląd: od razu). Na nowym rekordzie zapamiętuje
   * zadanie i uruchamia zwykły zapis (utworzenie) — resztę robi `saveMutation.onSuccess`.
   */
  function handleSqlButtonStart(procedureId: string): Promise<unknown> {
    if (!isCreate) return runs.startRun(procedureId);
    // Utworzenie już trwa (kliknięte „Zapisz” albo wcześniejszy przycisk) — drugi POST utworzyłby kontakt
    // drugi raz. Twardy bezpiecznik: stan mutacji czytany na żywo (`useIsMutating` odświeża się dopiero po
    // renderze, a kliknięcie może wyprzedzić render).
    if (
      queryClient.isMutating({ mutationKey: saveBeforeDecision.saveMutationKey, exact: true }) > 0
    ) {
      return Promise.resolve();
    }
    return new Promise<void>((resolve, reject) => {
      pendingCreateRunRef.current = { procedureId, settle: resolve };
      try {
        if (!handleSave()) settlePendingCreateRun();
      } catch (err) {
        // Wyjątek zanim zapis ruszył: zapamiętane zadanie nie może zostać w refie (wystartowałoby po
        // następnym, zwykłym zapisie) — czyścimy je i odrzucamy obietnicę (przycisk wraca do gotowości).
        pendingCreateRunRef.current = null;
        reject(err);
      }
    });
  }

  function handleCancel() {
    // Anulowanie gasi podświetlenia z nieudanej próby zapisu (ramka + tekst pod polem).
    fe.clearAll();
    if (isCreate) {
      navigate(withMenuItem('/contractor-contacts'));
      return;
    }
    setEditing(false);
    discardEdits();
    if (detailQ.data?.contact) {
      const c = detailQ.data.contact;
      setForm((prev) => ({
        ...prev,
        contractorId: c.contractorId,
        title: c.title,
        firstName: c.firstName,
        lastName: c.lastName,
        position: c.position,
        department: c.department,
        email: c.email,
        phone: c.phone,
        mobile: c.mobile,
        preferredChannel: c.preferredChannel,
        language: c.language,
        birthday: c.birthday,
        linkedinUrl: c.linkedinUrl,
        isPrimary: c.isPrimary,
        isDecisionMaker: c.isDecisionMaker,
        notes: c.notes,
      }));
    }
  }

  // Walidacja formatu wg TYPU pola (wspólna oś silnika) przed wysyłką: e-mail/URL/data
  // dla pól core (config, po stateKey) i dodatkowych (katalog, po slugu). Zwraca false
  // gdy zablokowano zapis (callsite resetuje pendingTab), true gdy poszło.
  function handleSave(): boolean {
    const fieldErrors = collectEngineFieldErrors(
      contactEngineConfig,
      form,
      kartotekaFields,
      extValues,
      t,
    );
    if (Object.keys(fieldErrors).length > 0) {
      fe.clearAll();
      for (const [key, msg] of Object.entries(fieldErrors)) {
        fe.set(key, msg);
      }
      toast.show({ variant: 'error', title: t('common.validationIssue.formToast') });
      return false;
    }
    saveMutation.mutate(form);
    return true;
  }

  if (!isCreate && detailQ.isLoading) {
    return (
      <div
        className="flex items-center justify-center p-12"
        data-testid="contractor-contact-detail-loading"
      >
        <Spinner />
      </div>
    );
  }

  if (!isCreate && detailQ.isError) {
    return (
      <div
        className="m-6 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700"
        data-testid="contractor-contact-detail-error"
      >
        {t('tenant.contractorContacts.detail.loadError')}
      </div>
    );
  }

  // Nowy kontakt: pola dopiero po wczytaniu wartości domyślnych (albo komunikat z „Spróbuj
  // ponownie”). Po wszystkich hookach.
  if (isCreate && recordDefaults.status !== 'ready') {
    return (
      <NewRecordDefaultsStatus defaults={recordDefaults} testIdPrefix="contractor-contact-detail" />
    );
  }

  const contact = detailQ.data?.contact;
  const isDeleted = contact?.gcrecord != null;
  // Rekord zablokowany na czas zadania przycisku SQL (decyzja „A”): aktywne zadanie albo właśnie
  // wysyłane zlecenie — „Edytuj” i „Usuń” nieaktywne (serwer odrzuca zapis i usunięcie tak czy inaczej).
  const recordLocked = runs.active !== null || runs.starting;
  const titleText = isCreate
    ? t('tenant.contractorContacts.detail.newTitle')
    : [contact?.title, contact?.firstName, contact?.lastName].filter(Boolean).join(' ') || '—';

  return (
    <div className="space-y-4 p-6" data-testid="contractor-contact-detail-page">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="flex items-start gap-3">
          <IconButton
            variant="ghost"
            size="sm"
            aria-label={t('common.back')}
            data-testid="contractor-contact-detail-back"
            onClick={() => navigate(withMenuItem('/contractor-contacts'))}
          >
            <ArrowLeft />
          </IconButton>
          <div>
            {isCreate ? (
              <h1
                className="text-2xl font-semibold text-text-primary"
                data-testid="contractor-contact-detail-create-title"
              >
                {titleText}
              </h1>
            ) : (
              <>
                <h1 className="text-2xl font-semibold text-text-primary">{titleText}</h1>
                <div className="mt-1 flex items-center gap-2 text-sm text-text-tertiary">
                  {/* Link do kontrahenta */}
                  {form.contractorId && (
                    <Link
                      to={`/contractors/${form.contractorId}`}
                      className="text-accent-700 hover:underline"
                    >
                      {contractorName || '—'}
                    </Link>
                  )}
                  {contact?.isPrimary && (
                    <Badge color="amber">
                      <Star size={10} aria-hidden="true" />{' '}
                      {t('tenant.contractorContacts.badge.primary')}
                    </Badge>
                  )}
                  {contact?.isDecisionMaker && (
                    <Badge color="blue">{t('tenant.contractorContacts.badge.decisionMaker')}</Badge>
                  )}
                  {isDeleted && (
                    <Badge color="red">{t('tenant.contractorContacts.badge.deleted')}</Badge>
                  )}
                </div>
              </>
            )}
          </div>
        </div>

        {/* Akcje */}
        <div className="flex items-center gap-2">
          {!editing && !isCreate && !isDeleted && (
            <>
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Pencil size={14} aria-hidden="true" />}
                onClick={() => setEditing(true)}
                disabled={recordLocked}
                data-testid="contractor-contact-detail-edit-btn"
              >
                {t('common.edit')}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<Trash2 size={14} aria-hidden="true" />}
                onClick={() => setConfirmDelete(true)}
                disabled={recordLocked}
                data-testid="contractor-contact-detail-delete-btn"
              >
                {t('common.delete')}
              </Button>
            </>
          )}
          {(editing || isCreate) && (
            <>
              <Button
                variant="primary"
                size="sm"
                leftIcon={<Save size={14} aria-hidden="true" />}
                onClick={() => handleSave()}
                disabled={saveMutation.isPending}
                data-testid="contractor-contact-detail-save-btn"
              >
                {t('common.save')}
              </Button>
              <Button
                variant="secondary"
                size="sm"
                leftIcon={<XIcon size={14} aria-hidden="true" />}
                onClick={handleCancel}
                disabled={saveMutation.isPending}
                data-testid="contractor-contact-detail-cancel-btn"
              >
                {t('common.cancel')}
              </Button>
            </>
          )}
        </div>
      </div>

      {/* Zadanie przycisku SQL na rekordzie (w kolejce lub w toku): rekordu nie da się zapisać ani
          usunąć, „Edytuj” i „Usuń” są nieaktywne; pasek znika po zakończeniu zadania. */}
      {runs.active ? (
        <RecordRunLockBanner
          run={runs.active}
          label={runs.labelFor(runs.active)}
          refreshFailed={runs.isError}
        />
      ) : null}

      {/* Sekcja: picker kontrahenta (create-mode) */}
      {isCreate && (
        <div className="rounded-lg border border-border-secondary bg-bg-primary p-5">
          <label className="mb-1 block text-xs text-text-tertiary">
            {t('tenant.contractorContacts.detail.contractorPickerLabel')} *
          </label>
          {prefilledContractorId ? (
            <div
              className="rounded-lg border border-transparent bg-bg-secondary px-3 py-1.5 text-sm text-text-primary"
              data-testid="contractor-contact-detail-contractor-prefilled"
            >
              {(contractorsQ.data ?? []).find((c) => c.id === prefilledContractorId)?.name ?? '—'}
            </div>
          ) : (
            <Dropdown
              value={form.contractorId}
              onChange={(v) => setForm({ ...form, contractorId: v })}
              data-testid="contractor-contact-detail-contractor-picker"
              className="w-full rounded-lg border border-border-secondary bg-bg-primary px-3 py-1.5 text-sm"
              options={[
                { value: '', label: t('common.selectPlaceholder') },
                ...(contractorsQ.data ?? []).map((c) => ({ value: c.id, label: c.name })),
              ]}
            />
          )}
        </div>
      )}

      {/* F12.N E1.T5b: tryb create + brak layoutu → sam ContactDataTab bez top-bara.
          W trybie edit z layoutem → RecursiveFormRenderer + tabRenderers per seed-slug. */}
      {/* F12.N D4 save-and-continue: w trybie create renderujemy OSOBNY `<Tabs>`
          nad stabilnym ContactDataTab — patrz ContractorDetail.tsx dla wzorca. */}
      {isCreate ? (
        <>
          {viewLevelFromLayout.map((el) => (
            <div key={el.id} className="mb-4">
              {tryRenderLayoutElement({
                element: {
                  id: el.id,
                  type: el.type,
                  settings: (el.settings ?? {}) as Record<string, unknown>,
                },
                children: [],
                mode: 'form',
              })}
            </div>
          ))}
          {tabsFromLayout && tabsFromLayout.length > 1 && (
            <Tabs
              items={tabsFromLayout.map((tt) => ({
                value: tt.slug,
                label: tt.title,
                content: null,
              }))}
              value={tab}
              onChange={setTab}
              idPrefix="contractor-contact-create-tabs"
              // mb-8 = dawny mb-4 + 16 px pustego panelu, którego Tabs przy `content: null` już nie rysuje.
              className="mb-8"
            />
          )}
          <ContactDataTab
            contextRenderers={contactContextRenderers}
            renderField={renderField}
            formData={layoutFormData}
          />
        </>
      ) : !layoutConfig ? (
        <ContactDataTab
          contextRenderers={contactContextRenderers}
          renderField={renderField}
          formData={layoutFormData}
        />
      ) : (
        <RecursiveFormRenderer
          layoutConfig={layoutConfig}
          activeTabId={tab}
          onTabChange={setTab}
          myRoleId={myRoleId}
          isSuperUser={isSuperUser}
          renderField={renderField}
          formData={layoutFormData}
          tabRenderers={{
            // Tylko „Dane" (typ fields). „Historia zmian" to przenośny blok — dispatch po
            // TYPIE przez contextRenderers. „Pola dodatkowe" nie mają osobnej zakładki: pola
            // układa w „Dane" silnik formatek.
            data: () => (
              <ContactDataTab
                contextRenderers={contactContextRenderers}
                renderField={renderField}
                formData={layoutFormData}
              />
            ),
          }}
          contextRenderers={contactContextRenderers}
        />
      )}

      <ConfirmDialog
        open={confirmDelete}
        title={t('common.confirmDelete')}
        message={titleText}
        onConfirm={() => {
          setConfirmDelete(false);
          deleteMutation.mutate();
        }}
        onClose={() => setConfirmDelete(false)}
      />
    </div>
  );
}
